<template>
  <div>
    <div class="heading heading--sm mt-4 mb-2" qaid="distributionCenter.heading">
      {{ warehouse.name }} ({{ warehouse.count }})
    </div>

    <!-- Callout for Current Availability -->
    <pdl-callout v-if="hasBackorders" kind="warning" qaid="distributionCenter.backorders.warning">
      <template #content>
        <span v-html="bp('primaryWarehouse')" />
      </template>
    </pdl-callout>

    <div v-if="showShippingInfo" class="mt-2 mb-5" qaid="distributionCenter.shipOptions">
      <fieldset class="form-fieldset">
        <legend class="form-fieldset__legend text-md">{{ bp('shipOrBackorderPrimary') }}</legend>
        <label :for="getInputId('ship')" class="radio text-md">
          <input :id="getInputId('ship')" v-model="warehouse.isBackOrdered" type="radio" :value="false" />
          <span class="control-indicator"></span>
          {{ bp('shipLines') }}
        </label>
        <label :for="getInputId('backorder')" class="radio text-md">
          <input :id="getInputId('backorder')" v-model="warehouse.isBackOrdered" type="radio" :value="true" />
          <span class="control-indicator"></span>
          {{ bp('backorderInPrimary') }}
        </label>
      </fieldset>
    </div>

    <!-- SKU GRID -->
    <table class="b2b-grid b2b-grid--collapse is-compact" qaid="saved-list-sku-grid">
      <!-- SKU Grid Headers -->
      <thead class="b2b-grid__header">
        <tr>
          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.split"></th>

          <th class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.sku">{{ bp('sku') }}</th>

          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.item">{{ bp('item') }}</th>

          <th class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.upc-ean">{{ bp('upc') }}</th>

          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.order-price">{{ bp('price') }}</th>

          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.qty">{{ bp('qty') }}</th>

          <th class="b2b-grid__cell b2b-grid__head text-right" qaid="sku-grid.subtotal">{{ bp('subtotal') }}</th>
        </tr>
      </thead>

      <!-- SKU Grid Body/Rows -->
      <tbody>
        <!-- For each Product/Row -->
        <template v-for="item in listData">
          <checkout-grid-row
            :key="item[itemIdKey]"
            :item="item"
            :is-backorder="warehouse.id === 'backOrdered'"
            :headers="tableHeaders"
            :for-print="forPrint"
            :checkout-step="checkoutStep"
          />

          <checkout-notes-row :key="`${partTypeKey}-${item[itemIdKey]}-notes`" :item="item" :for-print="forPrint" />
          <checkout-promo-row :key="`${partTypeKey}-${item[itemIdKey]}-promo`" :item="item" :for-print="forPrint" />
        </template>
      </tbody>
    </table>

    <div class="grid-x grid-margin-x mb-5">
      <div class="cell medium-6 large-8"></div>
      <div class="cell medium-6 large-4">
        <!-- Shipment Summary -->
        <ul class="block-list text-md">
          <li class="block-list__item" qaid="shipmentSummary.subtotal">
            <span>{{ bp('subtotal') }}</span>
            <span>{{ warehouse.subTotal }}</span>
          </li>
          <li v-if="!hasBackorders && !isDistributor" class="block-list__item" qaid="shipmentSummary.estimatedShipping">
            <span>{{ bp('estimatedShipping') }}</span>
            <span>{{ warehouse.formattedEstimatedShipping }}</span>
          </li>
          <li class="block-list__item" qaid="shipmentSummary.paymentTerms">
            <span class="flex-grow flex-shrink-0">{{ bp('paymentTerms') }}</span>
            <span class="flex-shrink flex-grow-0 text-right">{{ warehouse.paymentTerms }}</span>
          </li>
          <li class="block-list__item text-base font-medium" qaid="shipmentSummary.total">
            <span>{{ bp('warehouseTotal') }}</span>
            <span>{{ warehouse.total }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import pagination from '../../mixins/pagination';
import find from 'lodash/find';
import {mapState} from 'vuex';
import {PdlCallout} from '@pedal/pdl-callout';

export default {
  components: {
    PdlCallout,
  },
  mixins: [pagination],

  props: {
    hasShippingOptions: {
      type: Boolean,
      default: false,
    },
    itemIdKey: {
      type: String,
      default: '',
    },
    partTypeKey: {
      type: String,
      default: '',
    },
    itemIndex: {
      type: Number,
      default: 0,
    },
    checkoutStep: {
      type: String,
      default: '',
    },
    forPrint: {
      type: Boolean,
      default: false,
    },
    size: {
      type: Number,
      required: false,
      default: ACC.checkoutItemsPageSize || 50,
    },
    type: {
      type: String,
      default: '',
    },
    warehouse: {
      type: Object,
      default: () => {},
    },
    tableHeaders: {
      type: Array,
      default: () => [],
    },
    baseProperties: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    hasBackorders() {
      return this.warehouse.id === 'backOrdered';
    },

    showShippingInfo() {
      return this.hasShippingOptions && !this.warehouse.isPrimary && !this.hasBackorders;
    },

    listData() {
      return this.warehouse.entries;
    },

    ...mapState('backend', ['isDistributor']),
  },

  methods: {
    bp(name) {
      let baseProperty = find(this.baseProperties, (baseProperty) => baseProperty.name === name);
      return baseProperty ? baseProperty.label : '';
    },

    getCollapseItemName(partTypeKey, itemIdKey, index) {
      return `${partTypeKey}-${itemIdKey}-${index}`;
    },

    getInputId(suffix) {
      return `${this.partTypeKey}-${this.warehouse.id}-${suffix}`;
    },
  },
};
</script>
